import React, { useEffect, useState } from 'react';
import { decColor } from '../tokens';
import { IconAdvisor, IconCopy, IconDownload } from './icons';
import catalogo from '../datos/catalogo.json';

const CAT_N = catalogo.productos.length;
const CAT_CATS = new Set(catalogo.productos.map((p) => p.categoria)).size;

const DECS = ['info', 'pasa', 'aviso', 'escala'];

// Vista Junta · cajón a demanda ("ver debajo del capó", nunca fijo en la página del cliente)
// Contiene: panel Evidencia (tabla cronológica + filtro por decisión), Lead, exportación y AC12.
export const EvidenceLog = ({ log, fuente, hl, lead, advisor, caso, primaEditor, onApplyPrima, onClose }) => {
  const [filtro, setFiltro] = useState('todas');
  const [copiado, setCopiado] = useState(false);
  const [minV, setMinV] = useState(primaEditor ? String(primaEditor.min) : '');
  const [maxV, setMaxV] = useState(primaEditor ? String(primaEditor.max) : '');
  const json = JSON.stringify(log, null, 1);
  const rows = filtro === 'todas' ? log : log.filter((r) => r.dec === filtro);

  useEffect(() => {
    if (hl != null) {
      const el = document.getElementById(`log-row-${hl}`);
      if (el) el.scrollIntoView({ block: 'center' });
    }
  }, [hl, filtro]);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(json);
    } catch (e) {
      const ta = document.getElementById('log-export-text');
      if (ta) {
        ta.select();
        document.execCommand('copy');
      }
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1800);
  };

  const descargar = (contenido, tipo, ext) => {
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const blob = new Blob([contenido], { type: tipo });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mbm-evidencia-${caso}-${ts}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Recorrido completo: lead + paquete al asesor + log
  const exportJSON = () =>
    descargar(
      JSON.stringify({ caso, lead: lead || null, paquete_asesor: advisor || null, log }, null, 1),
      'application/json',
      'json',
    );

  const exportCSV = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = 'turno;journey;strike;agente;regla;fila;dec;txt;ts';
    const lines = log.map((r) =>
      [r.turno, r.journey, r.strike, r.agente, r.regla, r.fila, r.dec, r.txt, r.ts].map(esc).join(';'),
    );
    descargar([head, ...lines].join('\n'), 'text/csv;charset=utf-8', 'csv');
  };

  return (
    <aside className="evidence" data-testid="vista-junta">
      <header className="evidence-head">
        <div>
          <span className="vj-eyebrow">MBM · demo interactiva · debajo del capó</span>
          <strong>Vista Junta</strong>
          <span data-testid="catalogo-stats">
            {log.length} turnos · cada cifra con su fila o regla · catálogo {CAT_N} productos · {CAT_CATS} categorías
          </span>
        </div>
        <button type="button" className="evidence-close" onClick={onClose} data-testid="btn-cerrar-evidencia">
          Cerrar
        </button>
      </header>

      <div className="filtros" data-testid="evidencia-filtros">
        {['todas', ...DECS].map((d) => (
          <button
            key={d}
            type="button"
            className={`filtro-chip ${filtro === d ? 'on' : ''}`}
            style={d !== 'todas' ? { color: decColor[d] } : undefined}
            onClick={() => setFiltro(d)}
            data-testid={`filtro-${d}`}
          >
            {d} {d === 'todas' ? `(${log.length})` : `(${log.filter((r) => r.dec === d).length})`}
          </button>
        ))}
      </div>

      {fuente ? (
        <>
          <p className="evidence-fuente" data-testid="evidence-fuente">
            Origen consultado: <b>{fuente}</b>
          </p>
          {/* nota_mbm: solo para la junta, nunca en la pantalla del cliente */}
          {[...new Set(String(fuente).match(/MKT-\d+/g) || [])]
            .map((id) => catalogo.productos.find((p) => p.id === id))
            .filter((p) => p && p.nota_mbm)
            .map((p) => (
              <p className="nota-mbm" key={p.id} data-testid={`nota-mbm-${p.id}`}>
                <b>{p.id}</b> · {p.nota_mbm}
              </p>
            ))}
        </>
      ) : null}

      {lead && (lead.momento || lead.nombre) ? (
        <div className="lead-panel" data-testid="lead-panel">
          <span className="lead-k">Lead · capturado en el Journey 1</span>
          <dl>
            {lead.momento ? <div><dt>Momento</dt><dd>{lead.momento}</dd></div> : null}
            {lead.cuidar && lead.cuidar.length ? <div><dt>Cuidar</dt><dd>{lead.cuidar.join(' · ')}</dd></div> : null}
            {lead.quienes ? <div><dt>Depende de ti</dt><dd>{lead.quienes}</dd></div> : null}
            {lead.yapaga ? <div><dt>Ya paga</dt><dd>{lead.yapaga}</dd></div> : null}
            {lead.nombre ? <div><dt>Nombre</dt><dd>{lead.nombre}</dd></div> : null}
            {lead.tel ? <div><dt>WhatsApp</dt><dd>{lead.tel}</dd></div> : null}
            {lead.consent ? <div><dt>Consentimiento</dt><dd>Sí · {new Date(lead.consent.ts).toLocaleString('es-CO')}</dd></div> : null}
          </dl>
          <p className="lead-nota">Sin edad, sin cédula, sin ingresos.</p>
        </div>
      ) : null}

      <ol className="evidence-list" data-testid="evidencia-tabla">
        {rows.map((r) => (
          <li
            key={r.turno}
            id={`log-row-${r.turno}`}
            className={r.turno === hl ? 'hl' : ''}
            data-testid={`log-row-${r.turno}`}
          >
            <span className="log-turno">{String(r.turno).padStart(2, '0')}</span>
            <span className="log-badge" style={{ color: decColor[r.dec] || decColor.info }}>
              {r.dec === 'escala' ? <IconAdvisor size={13} color={decColor.escala} /> : null}
              {r.dec}
            </span>
            <span className="log-body">
              <span className="log-meta">
                S{r.strike} · {r.agente} · regla {r.regla} · fila {r.fila}
              </span>
              <span className="log-txt">{r.txt}</span>
              {r.ts ? <span className="log-ts">{new Date(r.ts).toLocaleTimeString('es-CO')}</span> : null}
            </span>
          </li>
        ))}
      </ol>

      <div className="evidence-actions">
        <button type="button" className="btn-ghost" onClick={copiar} data-testid="btn-copiar-log">
          <IconCopy size={15} /> {copiado ? 'Copiado' : 'Copiar log'}
        </button>
        <button type="button" className="btn-ghost" onClick={exportJSON} data-testid="btn-export-json">
          <IconDownload size={15} /> JSON
        </button>
        <button type="button" className="btn-ghost" onClick={exportCSV} data-testid="btn-export-csv">
          <IconDownload size={15} /> CSV
        </button>
      </div>

      {primaEditor ? (
        <div className="ac12" data-testid="ac12-editor">
          <span className="ac12-k">AC12 · prima en vivo · {primaEditor.id}</span>
          <div className="ac12-row">
            <label>
              prima_min
              <input
                type="number"
                value={minV}
                onChange={(e) => setMinV(e.target.value)}
                data-testid="ac12-min"
              />
            </label>
            <label>
              prima_max
              <input
                type="number"
                value={maxV}
                onChange={(e) => setMaxV(e.target.value)}
                data-testid="ac12-max"
              />
            </label>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => onApplyPrima(Number(minV), Number(maxV))}
              data-testid="ac12-aplicar"
            >
              Aplicar y reiniciar
            </button>
          </div>
          <p className="ac12-nota">
            Solo en memoria · al recargar la página vuelve al catálogo.
            {primaEditor.editada ? <span className="ac12-chip" data-testid="ac12-chip">editada en vivo · AC12</span> : null}
          </p>
        </div>
      ) : null}

      <textarea
        id="log-export-text"
        className="log-export"
        readOnly
        value={json}
        data-testid="log-export-text"
        aria-label="log exportable"
      />
    </aside>
  );
};
